import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FolderOpen, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function QRJoinPage({ user }) {
  const { folderId } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('joining'); // joining, success, error, already_member
  const [folderName, setFolderName] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!user || !folderId) return;
    joinFolder();
  }, [user, folderId]);

  const joinFolder = async () => {
    try {
      const res = await axios.post(`${API}/folders/${folderId}/qr-join`);
      const data = res.data;
      if (data.status === 'joined') {
        setStatus('success');
        setFolderName(data.folder_name);
        // Flag for streamlined onboarding
        localStorage.setItem('noyo_qr_join_folder_id', folderId);
        localStorage.setItem('noyo_qr_join_folder_name', data.folder_name || '');
      } else if (data.status === 'already_member') {
        setStatus('already_member');
        setFolderName(data.message || 'You are already a member');
        // Already a member — go straight to the folder (own or shared)
        const isOwnFolder = data.message === 'This is your own folder';
        setTimeout(() => {
          navigate(isOwnFolder ? `/stories/${folderId}` : `/stories/shared/${folderId}`, { replace: true });
        }, 1500);
        return;
      } else if (data.status === 'accepted') {
        setStatus('success');
        setFolderName('');
        localStorage.setItem('noyo_qr_join_folder_id', folderId);
      }
      // Navigate to the shared folder after a brief delay
      setTimeout(() => {
        navigate(`/stories/shared/${folderId}`, { replace: true });
      }, 2000);
    } catch (err) {
      setStatus('error');
      setError(err.response?.data?.detail || 'Failed to join folder');
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6" data-testid="qr-join-page">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-card rounded-2xl border border-border p-8 max-w-sm w-full text-center shadow-lg"
      >
        {status === 'joining' && (
          <>
            <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-foreground mb-2">Joining folder...</h2>
            <p className="text-sm text-muted-foreground">Setting up your collaboration access</p>
          </>
        )}
        {status === 'success' && (
          <>
            <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', stiffness: 200 }}>
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
            </motion.div>
            <h2 className="text-lg font-semibold text-foreground mb-2">You're in!</h2>
            {folderName && <p className="text-sm text-muted-foreground mb-4">Joined <strong>{folderName}</strong></p>}
            <p className="text-xs text-muted-foreground">Redirecting to the folder...</p>
          </>
        )}
        {status === 'already_member' && (
          <>
            <FolderOpen className="w-12 h-12 text-primary mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-foreground mb-2">Already a member</h2>
            <p className="text-sm text-muted-foreground mb-4">Redirecting to the folder...</p>
          </>
        )}
        {status === 'error' && (
          <>
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-foreground mb-2">Couldn't join</h2>
            <p className="text-sm text-muted-foreground mb-4">{error}</p>
            <button
              onClick={() => navigate('/stories', { replace: true })}
              className="bg-primary text-white rounded-full px-6 py-2 text-sm font-medium hover:bg-primary/80 transition-colors"
              data-testid="go-to-stories"
            >
              Go to Stories
            </button>
          </>
        )}
      </motion.div>
    </div>
  );
}

export default QRJoinPage;
